import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FiPackage, FiTruck, FiCheckCircle, FiClock, FiAlertCircle,
  FiChevronDown, FiChevronUp, FiCopy, FiCheck, FiExternalLink,
  FiArrowLeft, FiFileText, FiMapPin, FiPhone, FiRefreshCw,
  FiShoppingBag, FiLoader, FiX
} from 'react-icons/fi';
import './MyOrdersPage.css';
import { API_BASE_URL } from '../../config';

/* ─────────────────────────────────────────
   Invoice helper – number to words
───────────────────────────────────────── */
function numberToWords(num) {
  const a = ['','One ','Two ','Three ','Four ','Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  if ((num = num.toString()).length > 9) return 'overflow';
  let n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (+n[1] !== 0) ? (a[+n[1]] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
  str += (+n[2] !== 0) ? (a[+n[2]] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
  str += (+n[3] !== 0) ? (a[+n[3]] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
  str += (+n[4] !== 0) ? (a[+n[4]] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
  str += (+n[5] !== 0) ? ((str !== '') ? 'and ' : '') + (a[+n[5]] || b[n[5][0]] + ' ' + a[n[5][1]]) + 'Only ' : 'Only ';
  return str.replace(/\s+/g, ' ').trim();
}
function getAmountInWords(amount) {
  const ip = Math.floor(amount);
  const dp = Math.round((amount - ip) * 100);
  let r = numberToWords(ip);
  if (dp > 0) r += ` and ${numberToWords(dp).replace('Only', '')} Paise Only`;
  return r;
}

/* ─────────────────────────────────────────
   Status config
───────────────────────────────────────── */
const STATUS_CONFIG = {
  pending:          { label: 'Order Placed',      color: '#f59e0b', bg: '#fffbeb', icon: '🕐' },
  processing:       { label: 'Processing',         color: '#3b82f6', bg: '#eff6ff', icon: '⚙️' },
  shipped:          { label: 'Shipped',            color: '#8b5cf6', bg: '#f5f3ff', icon: '🚀' },
  out_for_delivery: { label: 'Out for Delivery',  color: '#f97316', bg: '#fff7ed', icon: '🚚' },
  delivered:        { label: 'Delivered',          color: '#10b981', bg: '#ecfdf5', icon: '✅' },
  cancelled:        { label: 'Cancelled',          color: '#ef4444', bg: '#fef2f2', icon: '❌' },
};

const STEPS = [
  { key: 'placed',     label: 'Order\nPlaced',      icon: '📋' },
  { key: 'processing', label: 'Processing',         icon: '⚙️' },
  { key: 'packed',     label: 'Packed',             icon: '📦' },
  { key: 'shipped',    label: 'Shipped',            icon: '🚀' },
  { key: 'out',        label: 'Out for\nDelivery',  icon: '🚚' },
  { key: 'delivered',  label: 'Delivered',          icon: '🏠' },
];

function getStepProgress(order) {
  const s = order.status;
  const pkg = order.packaging_status;
  if (s === 'delivered')        return 6;
  if (s === 'out_for_delivery') return 5;
  if (s === 'shipped')          return 4;
  if (pkg === 'Packed' || pkg === 'Dispatched') return 3;
  if (s === 'processing')       return 2;
  if (s === 'pending')          return 1;
  return 0;
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateTime(d) {
  return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

/* ─────────────────────────────────────────
   Main Component
───────────────────────────────────────── */
export default function MyOrdersPage({ currentUser, triggerNotification, convertPrice, selectedCurr }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'delivered' | 'cancelled'
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundModalOrderNum, setRefundModalOrderNum] = useState('');

  const API = API_BASE_URL;

  const fetchOrders = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/'); return; }
      const [ordRes, setRes] = await Promise.all([
        axios.get(`${API}/orders/history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders/settings/all`),
      ]);
      setOrders(ordRes.data.orders || []);
      if (setRes.data.settings) setSettings(setRes.data.settings);
    } catch (err) {
      console.error(err);
      triggerNotification?.('Failed to load your orders. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) { navigate('/'); return; }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    const eventSource = new EventSource(`${API}/orders/realtime`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'TRACKING_UPDATED') {
          triggerNotification?.('📦 Realtime Update: Your order has tracking updates!');
          fetchOrders(true);
        }
      } catch (err) {
        console.error('Failed to parse SSE event:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.warn('Real-time connection dropped. Retrying...', err);
    };

    return () => {
      eventSource.close();
    };
  }, [currentUser]);

  const handleCopy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  /* ── Invoice generation ─────────────────── */
  const handleInvoice = (order) => {
    const s = settings || {
      invoice_company_name: 'Orbifert Organic Pvt. Ltd.',
      invoice_company_address: 'ALYSSA, BEGONIA AND CLOVER, EMBASSY TECH VILLAGE, OUTER RING ROAD, BENGALURU, Karnataka, 560103',
      invoice_gstin: '29AABCF8078M1ZX',
      invoice_pan: 'AABCF8078M',
      invoice_cin: 'U51909KA2011PTC060489',
      invoice_contact_phone: '+91 7228064418',
      invoice_contact_email: 'billing@orbifertorganic.com',
      tax_rate: '5',
    };

    const taxRate = parseFloat(s.tax_rate || '5');
    const isIntra = (order.shipping_state || '').toLowerCase().includes('karnataka');
    let totalQty = 0, totalGross = 0, totalTaxable = 0, totalCGST = 0, totalSGST = 0, totalIGST = 0;

    const items = order.items || [];
    const tableRows = items.map((item, idx) => {
      const qty = item.quantity || 1;
      const taxable = parseFloat(item.price || 0) * qty;
      const gst = taxable * (taxRate / 100);
      const gross = taxable + gst;
      let cgstRate = 0, cgstAmt = 0, sgstRate = 0, sgstAmt = 0, igstRate = 0, igstAmt = 0;
      if (isIntra) { cgstRate = sgstRate = taxRate / 2; cgstAmt = sgstAmt = gst / 2; }
      else { igstRate = taxRate; igstAmt = gst; }
      totalQty += qty; totalGross += gross; totalTaxable += taxable;
      totalCGST += cgstAmt; totalSGST += sgstAmt; totalIGST += igstAmt;
      return `<tr>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:center;">${idx + 1}</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;"><strong>${item.title}</strong><br/><span style="font-size:11px;color:#555;">SKU: ${item.sku}</span></td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:center;">31010099</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:center;">${qty}</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${gross.toFixed(2)}</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹0.00</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${taxable.toFixed(2)}</td>
        ${isIntra
          ? `<td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">${cgstRate}%<br/>₹${cgstAmt.toFixed(2)}</td>
             <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">${sgstRate}%<br/>₹${sgstAmt.toFixed(2)}</td>`
          : `<td colspan="2" style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">${igstRate}%<br/>₹${igstAmt.toFixed(2)}</td>`}
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${gross.toFixed(2)}</td>
      </tr>`;
    }).join('');

    const shipping = parseFloat(order.shipping_amount || 0);
    let shippingRow = '';
    if (shipping > 0) {
      totalQty++; totalGross += shipping; totalTaxable += shipping;
      shippingRow = `<tr>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:center;">${items.length + 1}</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;"><strong>Shipping &amp; Delivery Charges</strong><br/><span style="font-size:11px;color:#555;">SAC: 996519</span></td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:center;">996519</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:center;">1</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${shipping.toFixed(2)}</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹0.00</td>
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${shipping.toFixed(2)}</td>
        ${isIntra
          ? `<td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">0%<br/>₹0.00</td><td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">0%<br/>₹0.00</td>`
          : `<td colspan="2" style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">0%<br/>₹0.00</td>`}
        <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${shipping.toFixed(2)}</td>
      </tr>`;
    }

    const grandTotal = parseFloat(order.total_amount);
    const amountInWords = getAmountInWords(grandTotal);
    const origin = window.location.origin;

    const html = `<!DOCTYPE html><html><head><title>Tax Invoice - ${order.order_number}</title>
    <style>
      body{font-family:Arial,sans-serif;margin:0;padding:20px;color:#333;font-size:13px;line-height:1.4;}
      .inv{max-width:800px;margin:0 auto;border:1px solid #ddd;padding:24px;}
      table{width:100%;border-collapse:collapse;}
      th{border:1px solid #ddd;padding:8px;text-align:center;font-size:12px;background:#f8fafc;font-weight:600;}
      .logo-row{display:flex;align-items:center;gap:10px;}
      .sig-box{text-align:right;}
      .footer{margin-top:24px;border-top:1px solid #eee;padding-top:12px;font-size:11px;text-align:center;color:#888;}
      @media print{body{padding:0;}.inv{border:none;padding:0;}}
    </style></head><body><div class="inv">
      <table style="margin-bottom:16px;"><tr>
        <td style="width:50%;vertical-align:top;">
          <div class="logo-row">
            <img src="${origin}/logo/Orbifert Logo.png" alt="Logo" style="height:54px;width:auto;object-fit:contain;" onerror="this.style.display='none'"/>
            <div style="display:flex;flex-direction:column;line-height:1.2;">
              <span style="font-family:'Montserrat',sans-serif;font-size:18px;font-weight:800;color:#363b6d;letter-spacing:1px;text-transform:uppercase;">ORBIFERT</span>
              <span style="font-family:'Montserrat',sans-serif;font-size:11px;font-weight:600;color:#666666;letter-spacing:0.5px;text-transform:uppercase;">ORGANIC PVT. LTD</span>
              <span style="font-family:'Montserrat',sans-serif;font-size:9.5px;font-weight:700;color:#f1921d;letter-spacing:0.5px;margin-top:2px;white-space:nowrap;">Reviving Soil, <span style="color:#1c6901;">Rewarding Farmers</span></span>
            </div>
          </div>
          <div style="margin-top:14px;font-size:12px;color:#555;">
            <strong>Billed From:</strong><br/>
            <strong>${s.invoice_company_name}</strong><br/>
            ${s.invoice_company_address.replace(/\n/g, '<br/>')}<br/>
            GSTIN: ${s.invoice_gstin}<br/>PAN: ${s.invoice_pan}<br/>CIN: ${s.invoice_cin}
          </div>
        </td>
        <td style="width:50%;text-align:right;vertical-align:top;">
          <h1 style="margin:0 0 6px;font-size:22px;color:#111;">TAX INVOICE</h1>
          <p style="margin:3px 0;font-size:12px;"><strong>Invoice #:</strong> INV-${order.order_number}</p>
          <p style="margin:3px 0;font-size:12px;"><strong>Invoice Date:</strong> ${fmtDate(order.created_at)}</p>
          <p style="margin:3px 0;font-size:12px;"><strong>Order ID:</strong> ${order.order_number}</p>
          <p style="margin:3px 0;font-size:12px;"><strong>Place of Supply:</strong> ${(order.shipping_state || 'KARNATAKA').toUpperCase()}</p>
          <p style="margin:3px 0;font-size:12px;"><strong>Reverse Charge:</strong> No</p>
        </td>
      </tr></table>

      <table style="border-top:1px solid #eee;border-bottom:1px solid #eee;padding:8px 0;margin-bottom:16px;"><tr>
        <td style="width:50%;vertical-align:top;padding:8px 8px 8px 0;font-size:12px;color:#555;">
          <div style="font-weight:700;color:#666;font-size:11px;text-transform:uppercase;margin-bottom:4px;">Billed To</div>
          <strong>${order.shipping_name}</strong><br/>
          ${order.company_name ? `<strong>Company:</strong> ${order.company_name}<br/>` : ''}
          ${order.gstin ? `<strong>GSTIN:</strong> ${order.gstin}<br/>` : ''}
          ${order.shipping_line1}${order.shipping_line2 ? '<br/>' + order.shipping_line2 : ''}<br/>
          ${order.shipping_city}, ${order.shipping_state} - ${order.shipping_pincode}<br/>
          Phone: ${order.shipping_phone}
        </td>
        <td style="width:50%;vertical-align:top;padding:8px 0 8px 8px;font-size:12px;color:#555;border-left:1px solid #eee;">
          <div style="font-weight:700;color:#666;font-size:11px;text-transform:uppercase;margin-bottom:4px;">Shipped From</div>
          <strong>${s.invoice_company_name}</strong><br/>
          ${s.invoice_company_address}<br/>
          GSTIN: ${s.invoice_gstin}
        </td>
      </tr></table>

      <table style="margin:16px 0;">
        <thead><tr>
          <th>S.No</th><th>Description</th><th>HSN/SAC</th><th>Qty</th>
          <th>Gross Amt</th><th>Discount</th><th>Taxable Value</th>
          ${isIntra ? '<th>CGST</th><th>SGST/UTGST</th>' : '<th colspan="2">IGST</th>'}
          <th>Total</th>
        </tr></thead>
        <tbody>
          ${tableRows}${shippingRow}
          <tr style="font-weight:bold;background:#f8fafc;">
            <td colspan="3" style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">Total:</td>
            <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:center;">${totalQty}</td>
            <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${totalGross.toFixed(2)}</td>
            <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹0.00</td>
            <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${totalTaxable.toFixed(2)}</td>
            ${isIntra
              ? `<td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${totalCGST.toFixed(2)}</td><td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${totalSGST.toFixed(2)}</td>`
              : `<td colspan="2" style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${totalIGST.toFixed(2)}</td>`}
            <td style="border:1px solid #ddd;padding:8px;font-size:12px;text-align:right;">₹${grandTotal.toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <table style="margin-top:16px;"><tr>
        <td style="width:60%;vertical-align:top;font-size:12px;">
          <p style="margin:0;"><strong>Amount in Words:</strong><br/>
          <em style="color:#555;text-transform:capitalize;">Rupees ${amountInWords}</em></p>
          <div style="margin-top:20px;font-size:11px;color:#777;">
            <strong>Terms &amp; Conditions:</strong><br/>
            1. Goods once sold will not be taken back without valid returns authorization.<br/>
            2. Keep this invoice for product warranty validation.
          </div>
        </td>
        <td style="width:40%;vertical-align:top;">
          <div class="sig-box">
            <p style="font-size:12px;font-weight:bold;margin:0 0 6px;">For ${s.invoice_company_name}</p>
            <div style="margin:8px 0;display:flex;justify-content:flex-end;">
              <img src="${origin}/signature/signature.jpg" alt="Signature"
                style="height:60px;width:auto;max-width:180px;object-fit:contain;"
                onerror="this.style.display='none'"/>
            </div>
            <p style="font-size:11px;color:#555;margin:0;">Authorized Signatory</p>
          </div>
        </td>
      </tr></table>

      <div class="footer">
        ${s.invoice_company_name} | ${s.invoice_company_address}<br/>
        📞 ${s.invoice_contact_phone} | ✉ ${s.invoice_contact_email}
      </div>
    </div></body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.open(); w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
    else alert('Pop-up blocked. Please allow pop-ups and try again.');
  };

  const toggleOrderExpansion = (order) => {
    const isCurrentlyExpanded = expandedId === order.id;
    if (!isCurrentlyExpanded) {
      setExpandedId(order.id);
      if (order.status === 'cancelled' && (order.payment_status === 'completed' || order.payment_status === 'paid' || order.payment_method !== 'COD')) {
        setRefundModalOrderNum(order.order_number);
        setShowRefundModal(true);
      }
    } else {
      setExpandedId(null);
    }
  };

  /* ── Filtering ──────────────────────────── */
  const filteredOrders = orders.filter(o => {
    if (filter === 'active') return !['delivered', 'cancelled'].includes(o.status);
    if (filter === 'delivered') return o.status === 'delivered';
    if (filter === 'cancelled') return o.status === 'cancelled';
    return true;
  });

  /* ── Render ─────────────────────────────── */
  if (loading) {
    return (
      <div className="mop-loading">
        <FiLoader className="mop-spinner" size={36} />
        <p>Loading your orders…</p>
      </div>
    );
  }

  return (
    <div className="mop-root">
      {/* ── Page Header ── */}
      <div className="mop-page-header">
        <div className="mop-page-header-inner">
          <button className="mop-back-btn" onClick={() => navigate('/')}>
            <FiArrowLeft size={16} /> Back to Store
          </button>
          <div className="mop-page-title-row">
            <div>
              <h1 className="mop-page-title">
                <FiShoppingBag size={22} /> My Orders &amp; Tracking
              </h1>
              <p className="mop-page-sub">Track shipments, download invoices and view your order history.</p>
            </div>
            <button className="mop-refresh-btn" onClick={fetchOrders}>
              <FiRefreshCw size={14} /> Refresh
            </button>
          </div>

          {/* Filter Tabs */}
          <div className="mop-filter-tabs">
            {[['all','All Orders'],['active','Active'],['delivered','Delivered'],['cancelled','Cancelled']].map(([v,l]) => (
              <button
                key={v}
                className={`mop-filter-tab ${filter === v ? 'active' : ''}`}
                onClick={() => setFilter(v)}
              >
                {l}
                <span className="mop-tab-count">
                  {v === 'all' ? orders.length
                    : v === 'active' ? orders.filter(o => !['delivered','cancelled'].includes(o.status)).length
                    : orders.filter(o => o.status === v).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Order List ── */}
      <div className="mop-body">
        <div className="mop-body-inner">
          {filteredOrders.length === 0 ? (
            <div className="mop-empty">
              <FiShoppingBag size={52} className="mop-empty-icon" />
              <h3>No orders found</h3>
              <p>You haven't placed any orders yet. Visit our shop to find premium organic farming products!</p>
              <button className="mop-shop-btn" onClick={() => navigate('/shop')}>Shop Now</button>
            </div>
          ) : (
            filteredOrders.map(order => {
              const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const isExpanded = expandedId === order.id;
              const progress = getStepProgress(order);
              const latestEvent = order.tracking_events?.length > 0
                ? order.tracking_events[order.tracking_events.length - 1]
                : null;

              return (
                <div key={order.id} className={`mop-order-card ${isExpanded ? 'expanded' : ''}`}>

                  {/* ── Order Card Header ── */}
                  <div className="mop-order-header">
                    <div className="mop-order-meta">
                      <div className="mop-order-meta-left">
                        <span className="mop-order-num-label">ORDER ID</span>
                        <span className="mop-order-num">{order.order_number}</span>
                      </div>
                      <div className="mop-order-meta-divider" />
                      <div className="mop-order-meta-item">
                        <span className="mop-meta-label">Placed On</span>
                        <span className="mop-meta-value">{fmtDate(order.created_at)}</span>
                      </div>
                      <div className="mop-order-meta-divider" />
                      <div className="mop-order-meta-item">
                        <span className="mop-meta-label">Total</span>
                        <span className="mop-meta-value mop-total">₹{parseFloat(order.total_amount).toFixed(2)}</span>
                      </div>
                      <div className="mop-order-meta-divider" />
                      <div className="mop-order-meta-item">
                        <span className="mop-meta-label">Payment</span>
                        <span className="mop-meta-value">{order.payment_method || 'Online'}</span>
                      </div>
                    </div>

                    <div className="mop-order-header-right">
                      {order.status === 'cancelled' && (order.payment_status === 'completed' || order.payment_status === 'paid' || order.payment_method !== 'COD') && (
                        <button
                          className="mop-refund-info-badge"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRefundModalOrderNum(order.order_number);
                            setShowRefundModal(true);
                          }}
                        >
                          ℹ️ Refund Notice
                        </button>
                      )}
                      <span className="mop-status-chip" style={{ color: cfg.color, background: cfg.bg }}>
                        {cfg.icon} {cfg.label}
                      </span>
                      <button
                        className="mop-expand-btn"
                        onClick={() => toggleOrderExpansion(order)}
                        aria-label="Toggle details"
                      >
                        {isExpanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                      </button>
                    </div>
                  </div>

                  {/* ── Products Row ── */}
                  <div className="mop-items-row">
                    {(order.items || []).map((item, i) => (
                      <div key={i} className="mop-item-chip">
                        <img
                          src={item.image || '/products/Super Novel.png'}
                          alt={item.title}
                          className="mop-item-img"
                        />
                        <div className="mop-item-info">
                          <span className="mop-item-name">{item.title}</span>
                          <span className="mop-item-sku">SKU: {item.sku}</span>
                          <span className="mop-item-qty">Qty: {item.quantity}</span>
                        </div>
                        <span className="mop-item-price">₹{parseFloat(item.price * (item.quantity || 1)).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  {/* ── Latest Status Bar ── */}
                  <div className="mop-status-bar">
                    <div className="mop-status-bar-left">
                      <span className="mop-status-icon-lg" style={{ color: cfg.color }}>{cfg.icon}</span>
                      <div>
                        <div className="mop-status-label" style={{ color: cfg.color }}>{cfg.label}</div>
                        {latestEvent && (
                          <div className="mop-status-sub">{latestEvent.status}{latestEvent.location ? ` · ${latestEvent.location}` : ''}</div>
                        )}
                        {order.estimated_delivery_date && order.status !== 'delivered' && (
                          <div className="mop-status-sub">
                            Est. Delivery: <strong>{fmtDate(order.estimated_delivery_date)}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mop-status-bar-actions">
                      <button className="mop-invoice-btn" onClick={() => handleInvoice(order)}>
                        <FiFileText size={13} /> Invoice
                      </button>
                      <button
                        className="mop-track-btn"
                        onClick={() => toggleOrderExpansion(order)}
                      >
                        {isExpanded ? 'Hide Details' : 'Track Order'}
                        {isExpanded ? <FiChevronUp size={13}/> : <FiChevronDown size={13}/>}
                      </button>
                    </div>
                  </div>

                  {/* ── Expanded Section ── */}
                  {isExpanded && (
                    <div className="mop-expanded">

                      {/* Delivery Stepper */}
                      <div className="mop-stepper-wrap">
                        <div className="mop-stepper">
                          <div className="mop-step-track">
                            <div className="mop-step-track-fill" style={{ width: `${((progress - 1) / 5) * 100}%` }} />
                          </div>
                          {STEPS.map((step, idx) => {
                            const stepNum = idx + 1;
                            const done = progress > stepNum;
                            const curr = progress === stepNum;
                            return (
                              <div key={idx} className={`mop-step ${done ? 'done' : curr ? 'current' : 'pending'}`}>
                                <div className="mop-step-circle">
                                  {done ? <FiCheckCircle size={16} /> : <span className="mop-step-icon">{step.icon}</span>}
                                  {curr && <span className="mop-step-pulse" />}
                                </div>
                                <span className="mop-step-label">{step.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Details Grid */}
                      <div className="mop-details-grid">

                        {/* Shipment Info */}
                        <div className="mop-detail-card">
                          <div className="mop-detail-card-title">
                            <FiTruck size={15} /> Shipment Info
                          </div>
                          <div className="mop-detail-rows">
                            <div className="mop-detail-row">
                              <span>Courier Partner</span>
                              <strong>{order.courier_partner || 'India Post'}</strong>
                            </div>
                            <div className="mop-detail-row">
                              <span>Tracking ID</span>
                              {order.tracking_id ? (
                                <div className="mop-tracking-id-wrap">
                                  <code className="mop-tracking-id">{order.tracking_id}</code>
                                  <button
                                    className="mop-copy-btn"
                                    onClick={() => handleCopy(order.id, order.tracking_id)}
                                    title="Copy"
                                  >
                                    {copiedId === order.id ? <FiCheck size={12} /> : <FiCopy size={12} />}
                                  </button>
                                </div>
                              ) : (
                                <span className="mop-pending-text">Pending dispatch</span>
                              )}
                            </div>
                            <div className="mop-detail-row">
                              <span>Est. Delivery</span>
                              <strong>
                                {order.estimated_delivery_date
                                  ? fmtDate(order.estimated_delivery_date)
                                  : 'TBD'}
                              </strong>
                            </div>
                          </div>
                          {order.tracking_link && order.tracking_id && (
                            <a
                              href={order.tracking_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mop-track-link-btn"
                            >
                              <FiExternalLink size={13} /> Track on India Post Website
                            </a>
                          )}
                        </div>

                        {/* Delivery Address */}
                        <div className="mop-detail-card">
                          <div className="mop-detail-card-title">
                            <FiMapPin size={15} /> Delivery Address
                          </div>
                          <div className="mop-address-block">
                            <p className="mop-addr-name">{order.shipping_name}</p>
                            <p>{order.shipping_line1}</p>
                            {order.shipping_line2 && <p>{order.shipping_line2}</p>}
                            {order.shipping_landmark && <p className="mop-addr-landmark">Near: {order.shipping_landmark}</p>}
                            <p><strong>{order.shipping_city}, {order.shipping_state} – {order.shipping_pincode}</strong></p>
                            <p className="mop-addr-phone"><FiPhone size={12} /> {order.shipping_phone}</p>
                          </div>
                        </div>

                        {/* Payment Info */}
                        <div className="mop-detail-card">
                          <div className="mop-detail-card-title">
                            <FiFileText size={15} /> Payment Summary
                          </div>
                          <div className="mop-detail-rows">
                            <div className="mop-detail-row">
                              <span>Payment Method</span>
                              <strong>{order.payment_method || 'Online'}</strong>
                            </div>
                            <div className="mop-detail-row">
                              <span>Subtotal</span>
                              <strong>₹{(parseFloat(order.total_amount) - parseFloat(order.shipping_amount || 0)).toFixed(2)}</strong>
                            </div>
                            <div className="mop-detail-row">
                              <span>Shipping</span>
                              <strong>{parseFloat(order.shipping_amount || 0) === 0 ? 'FREE' : `₹${parseFloat(order.shipping_amount).toFixed(2)}`}</strong>
                            </div>
                            <div className="mop-detail-row mop-grand-total-row">
                              <span>Grand Total</span>
                              <strong>₹{parseFloat(order.total_amount).toFixed(2)}</strong>
                            </div>
                          </div>
                          <button className="mop-invoice-full-btn" onClick={() => handleInvoice(order)}>
                            <FiFileText size={13} /> Download Tax Invoice
                          </button>
                        </div>
                      </div>

                      {/* Timeline */}
                      {order.tracking_events && order.tracking_events.length > 0 && (
                        <div className="mop-timeline-wrap">
                          <div className="mop-timeline-title">
                            <FiClock size={15} /> Shipment Journey
                          </div>
                          <div className="mop-timeline">
                            <div className="mop-timeline-stem" />
                            {[...order.tracking_events].reverse().map((ev, i) => (
                              <div key={ev.id || i} className={`mop-timeline-event ${i === 0 ? 'latest' : ''}`}>
                                <div className="mop-tl-dot" />
                                <div className="mop-tl-content">
                                  <div className="mop-tl-header">
                                    <span className="mop-tl-status">{ev.status}</span>
                                    <span className="mop-tl-time">{fmtDateTime(ev.created_at)}</span>
                                  </div>
                                  {ev.location && (
                                    <span className="mop-tl-loc">📍 {ev.location}</span>
                                  )}
                                  {ev.remarks && (
                                    <p className="mop-tl-remarks">{ev.remarks}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No events placeholder */}
                      {(!order.tracking_events || order.tracking_events.length === 0) && (
                        <div className="mop-no-events">
                          <FiAlertCircle size={20} />
                          <span>No tracking events yet. Your order is being processed.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Refund Information Modal */}
      {showRefundModal && (
        <div className="mop-refund-modal-overlay" onClick={() => setShowRefundModal(false)}>
          <div className="mop-refund-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="mop-refund-modal-header">
              <span className="mop-refund-modal-icon">💰</span>
              <h3>Refund Information</h3>
              <button className="mop-refund-close-btn" onClick={() => setShowRefundModal(false)}>
                <FiX size={18} />
              </button>
            </div>
            <div className="mop-refund-modal-body">
              <p className="mop-refund-order-text">
                Order Reference: <strong>{refundModalOrderNum}</strong>
              </p>
              <p className="mop-refund-main-message">
                This order was cancelled by the administrator. Since your payment was successfully processed, a full refund has been initiated.
              </p>
              <p className="mop-refund-sub-message">
                The refunded amount will be credited back to your original payment method (bank account, UPI, or card) within <strong>5–7 business days</strong>.
              </p>
            </div>
            <div className="mop-refund-modal-footer">
              <button className="mop-refund-gotit-btn" onClick={() => setShowRefundModal(false)}>
                Got It, Thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
